/**
 * DebugBus - shared in-memory event bus used by the optional debug SDK.
 *
 * The core SDK pushes log + fetch events into the bus. The bus is a no-op
 * unless `enable()` is called (done by `maybeActivateDebugSdk`). Once a
 * subscriber connects (the debug bundle), buffered events are drained first
 * and then new events stream in.
 *
 * A single instance is attached to `window.__dqDebugBus` so the dqtag and
 * oneIDSDK IIFE bundles share state even though they ship separately.
 */

export type DebugEvent =
  | {
      kind: "log";
      prefix: string;
      level: "debug" | "error";
      args: unknown[];
      ts: number;
    }
  | {
      kind: "fetch";
      phase: "start" | "end";
      method: string;
      url: string;
      body?: unknown;
      status?: number;
      error?: string;
      ts: number;
    }
  | {
      kind: "sdkInit";
      sdk: "dqtag" | "oneid";
      ts: number;
    };

export type DebugBusSubscriber = (event: DebugEvent) => void;

const RING_BUFFER_CAP = 500;

class DebugBus {
  private _events: DebugEvent[] = [];
  private _subscribers = new Set<DebugBusSubscriber>();
  private _enabled = false;

  public get enabled(): boolean {
    return this._enabled;
  }

  public enable(): void {
    this._enabled = true;
  }

  public disable(): void {
    this._enabled = false;
    this._events = [];
    this._subscribers.clear();
  }

  public push(event: DebugEvent): void {
    if (!this._enabled) return;
    if (this._events.length >= RING_BUFFER_CAP) {
      this._events.shift();
    }
    this._events.push(event);
    this._subscribers.forEach((fn) => {
      try {
        fn(event);
      } catch {
        // Subscribers must never break the SDK.
      }
    });
  }

  public subscribe(fn: DebugBusSubscriber): () => void {
    // Drain buffered events first so the new subscriber sees the past.
    for (const e of this._events) {
      try {
        fn(e);
      } catch {
        // ignore
      }
    }
    this._subscribers.add(fn);
    return () => {
      this._subscribers.delete(fn);
    };
  }

  public clear(): void {
    this._events = [];
  }

  /**
   * Return a shallow copy of the ring buffer. Used by the scenario engine
   * to replay past events into scenarios that opt into `scanHistory` — e.g.
   * scenario 01 needs to detect `sdkInit` events that fired before the user
   * even opened the debugger panel.
   */
  public snapshot(): DebugEvent[] {
    return this._events.slice();
  }
}

declare global {
  interface Window {
    __dqDebugBus?: DebugBus;
  }
}

const globalWindow = typeof window !== "undefined" ? window : undefined;

export const debugBus: DebugBus = (() => {
  if (globalWindow) {
    if (!globalWindow.__dqDebugBus) {
      globalWindow.__dqDebugBus = new DebugBus();
    }
    return globalWindow.__dqDebugBus;
  }
  return new DebugBus();
})();

export type { DebugBus };
